import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import GenericDataGrid from '../../../components/genericTable';
import NotificationSnackbar from '../../../components/notificationSnackbar';
import { createManagementTableSx } from '../../../styles/tableStyles';
import {
    managementCreateButtonSx,
    managementHeaderActionSx,
    managementHeaderContentSx,
    managementHeaderSx,
    managementPageSubtitleSx,
} from '../../../styles/managementStyles';
import { formFieldSx } from '../../../styles/formStyles';
import { buttonContentSx } from '../../../styles/dialogStyles';
import { compactTableActionButtonSx, tableDeleteButtonSx, tableViewButtonSx } from '../../../styles/actionStyles';
import { CropAliasModal } from './marketReferenceModals';
import { deleteCropAlias, getCropAliases, sortCropAliases } from './marketReference.service';

const textCellSx = { fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' };

const cardSx = { p: 3, borderRadius: '16px', border: '1px solid #DFE5F6', backgroundColor: '#FFFFFF' };

/**
 * Crop name mappings. DAREs type crop names freely (Dhan, Paddy, Rice); this list tells the engine
 * they are one crop, and which data.gov.in commodity carries its mandi prices.
 */
function CropNames() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    const load = useCallback(async () => {
        setLoading(true);
        const result = await getCropAliases();
        setRows(result.success ? result.data : []);
        if (!result.success) notify(result.error.message, 'error');
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        const sorted = sortCropAliases(rows);
        if (!needle) return sorted;
        return sorted.filter((row) =>
            [row.name, row.canonicalName, row.agmarknetCommodity].some((value) =>
                String(value || '').toLowerCase().includes(needle)
            )
        );
    }, [rows, search]);

    const handleDelete = async (id) => {
        const result = await deleteCropAlias(id);
        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        await load();
        notify('Crop name removed.');
    };

    const columns = [
        {
            field: 'name',
            headerName: 'Name used',
            flex: 1,
            minWidth: 160,
            sortable: false,
            renderCell: (params) => <Typography sx={{ ...textCellSx, fontWeight: 600 }}>{params.value}</Typography>,
        },
        {
            field: 'canonicalName',
            headerName: 'Means crop',
            flex: 1,
            minWidth: 160,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{params.value}</Typography>,
        },
        {
            field: 'agmarknetCommodity',
            headerName: 'Mandi commodity',
            flex: 1.2,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{params.value || '-'}</Typography>,
        },
        {
            field: 'notes',
            headerName: 'Notes',
            flex: 1.2,
            minWidth: 180,
            sortable: false,
            renderCell: (params) => (
                <Typography
                    sx={{ ...textCellSx, fontSize: 14, color: '#625B71', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {params.value || '-'}
                </Typography>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <Button
                        sx={{ ...tableViewButtonSx, ...compactTableActionButtonSx }}
                        onClick={() => {
                            setEditing(params.row);
                            setModalOpen(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        sx={{ ...tableDeleteButtonSx, ...compactTableActionButtonSx }}
                        onClick={() => handleDelete(params.row.id)}
                    >
                        Delete
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <>
            <Box sx={managementHeaderSx}>
                <Box sx={managementHeaderContentSx}>
                    <Typography sx={managementPageSubtitleSx}>
                        Which crop each name means. MSP prices, soil moisture thresholds and mandi prices are found
                        through this list, whatever name a DARE typed.
                    </Typography>
                </Box>
                <Box sx={managementHeaderActionSx}>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setEditing(null);
                            setModalOpen(true);
                        }}
                        sx={{ ...managementCreateButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { xs: '100%', md: 204 } }}
                    >
                        <Box component="span" sx={buttonContentSx}>
                            <Icon icon="tabler:plus" width="18" height="18" />
                            <span>ADD NAME</span>
                        </Box>
                    </Button>
                </Box>
            </Box>

            {rows.length > 0 ? (
                <Box sx={{ mb: 2, maxWidth: 360 }}>
                    <TextField
                        fullWidth
                        size="small"
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                            setPage(0);
                        }}
                        placeholder="Search names or commodities"
                        sx={formFieldSx}
                    />
                </Box>
            ) : null}

            {rows.length === 0 && !loading ? (
                <Paper elevation={0} sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
                    <Icon icon="tabler:plant-2" width="40" height="40" color="#94A3B8" />
                    <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1C1B1F', mt: 2, mb: 1 }}>
                        No crop names mapped yet
                    </Typography>
                    <Typography sx={{ color: '#625B71', maxWidth: 620, mx: 'auto' }}>
                        Until they are, a crop's reference data is only found when the farmer's crop name matches it
                        exactly.
                    </Typography>
                </Paper>
            ) : (
                <GenericDataGrid
                    rows={filtered.slice(page * size, page * size + size)}
                    columns={columns}
                    rowCount={filtered.length}
                    page={page}
                    pageSize={size}
                    loading={loading}
                    dataGridSx={createManagementTableSx()}
                    tableMinWidth={980}
                    rowHeight={60}
                    onPaginationChange={(nextPage, nextSize) => {
                        setPage(nextPage);
                        setSize(nextSize);
                    }}
                />
            )}

            <CropAliasModal
                open={modalOpen}
                alias={editing}
                existing={rows}
                onClose={() => setModalOpen(false)}
                onSaved={async () => {
                    setModalOpen(false);
                    await load();
                    notify('Crop name saved.');
                }}
            />

            <NotificationSnackbar
                open={snackbar.open}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </>
    );
}

export default CropNames;
